import { DcfComponentBase } from './dcf-base.js';
import { InlineEditor } from './inline-editor.js';

class DcfModel extends DcfComponentBase {

	widgetWillMount() {
		// ══════════════════════════════════════════════
		//  Shared computed fields (from base class)
		// ══════════════════════════════════════════════

		this._defineWaccComputed();
		this._defineProjectionComputed();
		this._defineSumPvFcf();
		this._defineTvComputed();
		this._defineProjectionSubtitle();
		this._defineSensitivityComputed();

		// ══════════════════════════════════════════════
		//  DCF-specific: EV Bridge Computed Fields
		// ══════════════════════════════════════════════

		// Equity Value in ReportCurrency
		this.defineComputed('equityValue', () => {
			const s = this.state;
			return s.enterpriseValue
				- s.totalDebt
				- s.minorityInterest
				- s.preferredEquity
				+ s.cash
				+ s.equityInvestments
				+ s.nonOperatingAssets;
		});

		// Equity Value converted to PriceCurrency
		this.defineComputed('equityValueInPriceCurrency', () => {
			return this.state.equityValue * this.state.fxRate;
		});

		// Implied Share Price in PriceCurrency
		this.defineComputed('impliedSharePrice', () => {
			const shares = this.state.dilutedSharesOutstanding;
			if (shares === 0) return 0;
			return this.state.equityValue * this.state.fxRate / shares;
		});

		// ══════════════════════════════════════════════
		//  DCF-specific: Headline Computed Fields
		// ══════════════════════════════════════════════

		this.defineComputed('upsideBadge', () => {
			const current = this.state.currentSharePrice;
			if (current === 0) return '—';
			const upside = (this.state.impliedSharePrice / current - 1) * 100;
			const sign = upside >= 0 ? '+' : '';
			const arrow = upside >= 0 ? '▲' : '▼';
			return `${sign}${upside.toFixed(1)}% ${arrow}`;
		});

		this.defineComputed('metaSummary', () => {
			const s = this.state;
			const parts = [
				`Valuation Date: ${this._formatDate(s.valuationDate)}`,
				s.dcfApproach.toUpperCase(),
				`${s.projectionYears}-Year Projection`,
				s.midYearConvention ? 'Mid-Year Convention' : 'Year-End Convention'
			];
			if (this._isCrossCurrency()) {
				parts.push(`${s.reportCurrency} (report) → ${s.priceCurrency} (price) @ ${s.fxRate.toFixed(4)}`);
			} else {
				parts.push(`${s.reportCurrency}, ${s.units}`);
			}
			return parts.join(' · ');
		});

		// ══════════════════════════════════════════════
		//  DCF-specific: Terminal Value display helpers
		// ══════════════════════════════════════════════

		this.defineComputed('tvMethodDisplay', () => {
			const s = this.state;
			switch (s.terminalValueMethod) {
				case 'blended':
					return `Blended (${(s.blendWeightPerpetuity * 100).toFixed(0)}% / ${(s.blendWeightExit * 100).toFixed(0)}%)`;
				case 'perpetuity_growth': return 'Perpetuity Growth';
				case 'exit_multiple': return 'Exit Multiple';
				default: return s.terminalValueMethod;
			}
		});

		this.defineComputed('waccBlockTitle', () => {
			return this.state.dcfApproach === 'fcfe' ? 'Cost of Equity' : 'WACC';
		});
	}

	// ══════════════════════════════════════════════
	//  Lifecycle hooks
	// ══════════════════════════════════════════════

	widgetDidMount() {
		this._updateVisibility();
		this._updateWaterfallBars();
		this._updateSensitivityCells();
		this._updateHideIfZeroRows();
		this._updateUpsideBadgeClass();
		this._updateTvPercentIndicator();

		InlineEditor.attach(this.root, this.state, this.fields);
	}

	widgetContentChanged() {
		this._updateVisibility();
		this._updateWaterfallBars();
		this._updateSensitivityCells();
		this._updateHideIfZeroRows();
		this._updateUpsideBadgeClass();
		this._updateTvPercentIndicator();
	}

	// ══════════════════════════════════════════════
	//  DCF Sensitivity: implied share price per cell
	// ══════════════════════════════════════════════

	_calcSensitivityCell(table, rowVal, colVal) {
		const s = this.state;
		const wacc = rowVal;
		const fxRate = s.fxRate || 1;

		// Recalculate discount factors and PV of FCFs with overridden WACC
		let sumPvFcf = 0;
		let lastUfcf = 0;
		let lastDiscountPeriod = 0;
		let lastEbitda = 0;
		let lastEbit = 0;
		let lastRevenue = 0;

		for (const p of s.periods) {
			if (p.isActual) continue;
			if (p.unleveredFcf == null || p.discountPeriod == null) continue;

			const df = Math.pow(1 + wacc, -p.discountPeriod);
			sumPvFcf += p.unleveredFcf * df;
			lastUfcf = p.unleveredFcf;
			lastDiscountPeriod = p.discountPeriod;
			lastEbitda = p.ebitda || 0;
			lastEbit = p.ebit || 0;
			lastRevenue = p.revenue || 0;
		}

		// Terminal value with overridden column parameter
		let tvUndiscounted = 0;
		if (table.colFormat === 'pct') {
			const g = colVal;
			const denom = wacc - g;
			if (denom > 0) {
				const termFcf = lastUfcf * (1 + g);
				const tvPerp = termFcf / denom;
				const exitVal = this._getExitMetricValueFor(lastEbitda, lastEbit, lastRevenue, lastUfcf);
				const tvExit = exitVal * s.exitMultiple;
				tvUndiscounted = this._blendTv(s, tvPerp, tvExit);
			}
		} else {
			const exitMult = colVal;
			const exitVal = this._getExitMetricValueFor(lastEbitda, lastEbit, lastRevenue, lastUfcf);
			const tvExit = exitVal * exitMult;
			const g = s.terminalGrowthRate;
			const denom = wacc - g;
			const tvPerp = denom > 0 ? (lastUfcf * (1 + g)) / denom : 0;
			tvUndiscounted = this._blendTv(s, tvPerp, tvExit);
		}

		// Discount TV
		const tvPeriod = s.midYearConvention ? lastDiscountPeriod + 0.5 : lastDiscountPeriod;
		const tvDf = Math.pow(1 + wacc, -tvPeriod);
		const pvTv = tvUndiscounted * tvDf;

		// EV → Equity → Price
		const ev = sumPvFcf + pvTv;
		const equity = ev - s.totalDebt - s.minorityInterest - s.preferredEquity
			+ s.cash + s.equityInvestments + s.nonOperatingAssets;
		const equityInPriceCcy = equity * fxRate;
		return s.dilutedSharesOutstanding !== 0 ? equityInPriceCcy / s.dilutedSharesOutstanding : 0;
	}

	_getExitMetricValueFor(ebitda, ebit, revenue, ufcf) {
		switch (this.state.exitMetric) {
			case 'EV/EBITDA': return ebitda;
			case 'EV/EBIT': return ebit;
			case 'EV/Revenue': return revenue;
			case 'EV/FCF': return ufcf;
			default: return ebitda;
		}
	}

	// ══════════════════════════════════════════════
	//  DCF-specific UI Update Methods
	// ══════════════════════════════════════════════

	_updateVisibility() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const s = this.state;

		// TV cards
		const showPerp = s.terminalValueMethod === 'perpetuity_growth' || s.terminalValueMethod === 'blended';
		const showExit = s.terminalValueMethod === 'exit_multiple' || s.terminalValueMethod === 'blended';

		root.querySelectorAll('[data-tv-method="perpetuity_growth"]').forEach(el => {
			el.style.display = showPerp ? '' : 'none';
		});
		root.querySelectorAll('[data-tv-method="exit_multiple"]').forEach(el => {
			el.style.display = showExit ? '' : 'none';
		});

		this._updateBlendWeightVisibility();

		// WACC columns — hide debt & structure for FCFE
		const isFcfe = s.dcfApproach === 'fcfe';
		root.querySelectorAll('.wacc-col-debt, .wacc-col-structure').forEach(el => {
			el.style.display = isFcfe ? 'none' : '';
		});

		this._updateCrossCurrencyVisibility();

		// FX conversion line in waterfall
		const isCross = this._isCrossCurrency();
		root.querySelectorAll('.wf-fx-line').forEach(el => {
			el.style.display = isCross ? '' : 'none';
		});
		root.querySelectorAll('.wacc-fx-converted').forEach(el => {
			el.style.display = isCross ? '' : 'none';
		});
	}

	_updateWaterfallBars() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const ev = Math.abs(this.state.enterpriseValue) || 1;

		root.querySelectorAll('[data-wf-bar-var]').forEach(bar => {
			const varName = bar.dataset.wfBarVar;
			const val = this.state[varName] || 0;
			const pct = Math.min(Math.abs(val) / ev * 100, 100);
			bar.style.width = pct + '%';
		});

		root.querySelectorAll('[data-wf-var]').forEach(row => {
			const varName = row.dataset.wfVar;
			const isNeg = row.dataset.wfNegative === '1';
			const val = this.state[varName] || 0;
			const isSubtotal = row.classList.contains('row-subtotal');

			const bar = row.querySelector('.bar-fill');
			if (bar) {
				bar.classList.remove('bar-positive', 'bar-negative', 'bar-subtotal');
				if (isSubtotal) bar.classList.add('bar-subtotal');
				else if (isNeg || val < 0) bar.classList.add('bar-negative');
				else bar.classList.add('bar-positive');
			}

			if (isNeg && val === 0 && !isSubtotal) {
				row.style.display = 'none';
			} else {
				row.style.display = '';
			}
		});
	}

	_updateSensitivityCells() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const currentPrice = this.state.currentSharePrice;

		this.state.sensitivityTables.forEach((st, tIdx) => {
			const tableEl = root.querySelector(`.sensitivity-heatmap[data-table-index="${tIdx}"]`);
			if (!tableEl) return;

			tableEl.querySelectorAll('th[data-col]').forEach(th => {
				const c = parseInt(th.dataset.col);
				if (c < st.colValues.length) {
					th.textContent = this._formatAxisValue(st.colValues[c], st.colFormat);
					th.className = c === st.baseColIndex ? 'r base-axis' : 'r';
					th.style.display = '';
				} else {
					th.style.display = 'none';
				}
			});

			tableEl.querySelectorAll('tr[data-row]').forEach(tr => {
				const r = parseInt(tr.dataset.row);
				if (r >= st.rowValues.length) { tr.style.display = 'none'; return; }
				tr.style.display = '';
				tr.className = r === st.baseRowIndex ? 'base-row' : '';

				const rowHeader = tr.querySelector('[data-row-header]');
				if (rowHeader) {
					rowHeader.textContent = this._formatAxisValue(st.rowValues[r], st.rowFormat);
					rowHeader.className = r === st.baseRowIndex ? 'axis-label base-axis' : 'axis-label';
				}

				tr.querySelectorAll('td[data-col]').forEach(td => {
					const c = parseInt(td.dataset.col);
					if (c >= st.colValues.length) { td.style.display = 'none'; return; }
					td.style.display = '';

					const val = st.matrix && st.matrix[r] && st.matrix[r][c] != null ? st.matrix[r][c] : 0;
					td.textContent = Math.round(val).toLocaleString();

					const isBase = r === st.baseRowIndex && c === st.baseColIndex;
					const heatClass = this._getHeatmapClass(val, currentPrice);
					td.className = `r ${heatClass}${isBase ? ' base-cell' : ''}`;
				});
			});
		});
	}

	_updateUpsideBadgeClass() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const badge = root.querySelector('.upside-badge');
		if (!badge) return;

		const current = this.state.currentSharePrice;
		const implied = this.state.impliedSharePrice;
		badge.classList.remove('badge-upside', 'badge-downside');
		badge.classList.add(implied >= current ? 'badge-upside' : 'badge-downside');
	}

	_getHeatmapClass(value, currentPrice) {
		if (currentPrice === 0) return 'heat-neutral';
		const ratio = value / currentPrice;
		if (ratio >= 1.3) return 'heat-strong-up';
		if (ratio >= 1.1) return 'heat-up';
		if (ratio >= 0.95) return 'heat-neutral';
		if (ratio >= 0.8) return 'heat-down';
		return 'heat-strong-down';
	}
}

Tango.registerComponent(DcfModel);